import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Pause, Play, RotateCcw, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { H1, Sub, IconBtn } from "./_shared";

export default function UsersSection() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (role) params.set("role", role);
    const { data } = await api.get(`/admin/users?${params.toString()}`);
    setUsers(data);
  }, [q, role]);
  useEffect(() => { load(); }, [load]);

  const suspend = async (uid) => {
    try { const { data } = await api.post(`/admin/users/${uid}/suspend`); toast.success(data.is_suspended ? "Suspended" : "Reactivated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async (uid, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="cp-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><H1>Users</H1><Sub>Search, edit, suspend, delete or reset password.</Sub></div>
        <button onClick={() => setCreating(true)} data-testid="cp-user-new" className="pp-btn-primary px-4 py-2 rounded-lg flex items-center gap-2"><Plus size={14} /> New user</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute start-3 top-3.5 text-zinc-500" />
          <input data-testid="cp-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pp-input ps-9" />
        </div>
        <select data-testid="cp-user-role" value={role} onChange={(e) => setRole(e.target.value)} className="pp-input max-w-xs">
          <option value="">All roles</option>
          <option value="player">Player</option>
          <option value="coach">Coach</option>
          <option value="club_admin">Club</option>
          <option value="promoter">Promoter</option>
          <option value="federation">Federation</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="mt-6 pp-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="cp-users-table">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-white/5">
            <tr>
              <th className="text-start px-4 py-3">User</th>
              <th className="text-start px-4 py-3">Role</th>
              <th className="text-start px-4 py-3">Status</th>
              <th className="text-start px-4 py-3">ELO</th>
              <th className="text-start px-4 py-3">Country</th>
              <th className="text-end px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5" data-testid={`cp-user-row-${u.id}`}>
                <td className="px-4 py-2">
                  <div className="font-medium">{u.first_name} {u.last_name}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </td>
                <td className="px-4 py-2"><span className="pp-chip">{u.role}</span></td>
                <td className="px-4 py-2">
                  {u.is_suspended ? <span className="text-[#FF3B30] text-xs">Suspended</span> : <span className="text-[#00E676] text-xs">Active</span>}
                </td>
                <td className="px-4 py-2 font-mono">{u.elo || 1000}</td>
                <td className="px-4 py-2 text-zinc-400">{u.country || "—"}</td>
                <td className="px-4 py-2 text-end">
                  <div className="inline-flex gap-1">
                    <IconBtn onClick={() => setEditing(u)} title="Edit" data-testid={`cp-user-edit-${u.id}`}><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => suspend(u.id)} title={u.is_suspended ? "Unsuspend" : "Suspend"} data-testid={`cp-user-suspend-${u.id}`}>{u.is_suspended ? <Play size={14} /> : <Pause size={14} />}</IconBtn>
                    <IconBtn onClick={() => setResetting(u)} title="Reset password" data-testid={`cp-user-reset-${u.id}`}><RotateCcw size={14} /></IconBtn>
                    <IconBtn onClick={() => del(u.id, u.email)} title="Delete" danger data-testid={`cp-user-del-${u.id}`}><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-10">No users.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <UserCreateDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editing && <UserEditDialog user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function UserCreateDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", role: "player", country: "", city: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", f); toast.success("User created"); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="cp-user-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" data-testid="cp-uc-firstname" required placeholder="First name" value={f.first_name} onChange={set("first_name")} />
            <input className="pp-input" data-testid="cp-uc-lastname" required placeholder="Last name" value={f.last_name} onChange={set("last_name")} />
          </div>
          <input className="pp-input" data-testid="cp-uc-email" required type="email" placeholder="Email" value={f.email} onChange={set("email")} />
          <input className="pp-input" data-testid="cp-uc-password" required type="password" minLength={6} placeholder="Password (min 6)" value={f.password} onChange={set("password")} />
          <select className="pp-input" data-testid="cp-uc-role" value={f.role} onChange={set("role")}>
            <option value="player">Player</option><option value="coach">Coach</option><option value="club_admin">Club admin</option>
            <option value="promoter">Promoter</option><option value="federation">Federation</option><option value="admin">Admin</option>
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" placeholder="Country" value={f.country} onChange={set("country")} />
            <input className="pp-input" placeholder="City" value={f.city} onChange={set("city")} />
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
            <button data-testid="cp-uc-submit" className="pp-btn-primary px-4 py-2 rounded-lg">Create</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserEditDialog({ user, onClose, onSaved }) {
  const [f, setF] = useState({
    first_name: user.first_name || "", last_name: user.last_name || "",
    role: user.role, country: user.country || "", city: user.city || "",
    bio: user.bio || "", elo: user.elo || 1000,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.patch(`/admin/users/${user.id}`, { ...f, elo: Number(f.elo) }); toast.success("Saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="cp-user-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit {user.email}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" placeholder="First name" value={f.first_name} onChange={set("first_name")} />
            <input className="pp-input" placeholder="Last name" value={f.last_name} onChange={set("last_name")} />
          </div>
          <select className="pp-input" data-testid="cp-ue-role" value={f.role} onChange={set("role")}>
            <option value="player">Player</option><option value="coach">Coach</option><option value="club_admin">Club admin</option>
            <option value="promoter">Promoter</option><option value="federation">Federation</option><option value="admin">Admin</option>
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" placeholder="Country" value={f.country} onChange={set("country")} />
            <input className="pp-input" placeholder="City" value={f.city} onChange={set("city")} />
          </div>
          <input className="pp-input" type="number" placeholder="ELO" value={f.elo} onChange={set("elo")} />
          <textarea className="pp-input" rows={2} placeholder="Bio" value={f.bio} onChange={set("bio")} />
          <DialogFooter>
            <button type="button" onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
            <button data-testid="cp-ue-submit" className="pp-btn-primary px-4 py-2 rounded-lg">Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/users/${user.id}/reset-password`, { password: pw }); toast.success("Password reset"); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="cp-reset-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reset password — {user.email}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input className="pp-input" type="password" minLength={6} required placeholder="New password (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="cp-reset-pw" />
          <DialogFooter>
            <button type="button" onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
            <button data-testid="cp-reset-submit" className="pp-btn-primary px-4 py-2 rounded-lg">Reset</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
